import React, { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import LabeledTextField from "../../Components/Common/LabeledTextField";
import StatusAlert from "../../Components/Common/StatusAlert";
import { Button, buttonVariants } from "../../shadcn/components/ui/button";
import { Spinner } from "../../shadcn/components/ui/spinner";
import { Switch } from "../../shadcn/components/ui/switch";
import { useAuthContext } from "../../context/AuthContext";
import { useConsoleHelpLinks } from "../../context/ConsoleHelpLinksContext";
import { useTranslation } from "../../context/Internationalization";
import useGraphql from "../../Hooks/useGraphql";
import { isSuperAdmin } from "../../utility/Utils";
import { CONSOLE_HELP_PAGES, createConsoleHelpDraft, hasInvalidConsoleHelpDraft, normalizeConsoleHelpUrl } from "../../utility/consoleHelpLinks";
import { UPDATE_CONSOLE_HELP_LINKS } from "../../graphql/consoleHelpGraphql";
import PageHeader from "../../Components/Common/PageHeader";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../../shadcn/components/ui/card";
import { Field, FieldTitle } from "../../shadcn/components/ui/field";

const emptyState = (scope = null) => ({ scope, draft: null, revision: 0, original: "", busy: false, conflict: false, error: null, saved: false });

export default function SuperAdminHelpLinks() {
  const auth = useAuthContext();
  const help = useConsoleHelpLinks();
  const { t } = useTranslation();
  const { axiosGraphQL } = useGraphql();
  const scope = help?.scope;
  const canEdit = Boolean(isSuperAdmin(auth.authState?.user) && !auth.authState?.impersonate?._id && scope);
  const current = useRef({ scope, canEdit });
  current.current = { scope, canEdit };
  const mounted = useRef(true);
  const operation = useRef(0);
  const [state, setState] = useState(() => emptyState());
  const visible = state.scope === scope ? state : { draft: null, busy: false };

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; operation.current += 1; };
  }, []);

  useEffect(() => {
    operation.current += 1;
    setState(previous => canEdit && previous.scope === scope ? previous : emptyState(scope));
  }, [scope, canEdit]);

  useEffect(() => {
    if (!canEdit || !help?.configuration) return;
    setState(previous => {
      // Refresh clean editors while preserving unsaved work and active operations.
      const dirty = previous.draft && JSON.stringify(previous.draft) !== previous.original;
      if (previous.scope === scope && previous.draft && (dirty || previous.conflict || previous.busy)) return previous;
      const draft = createConsoleHelpDraft(help.configuration);
      return { scope, draft, revision: help.configuration.revision, original: JSON.stringify(draft), busy: false, conflict: false, error: null, saved: false };
    });
  }, [canEdit, scope, help?.configuration]);

  const run = async (save) => {
    if (!canEdit || visible.busy || (save && (!visible.draft || visible.conflict || hasInvalidConsoleHelpDraft(visible.draft)))) return;
    const requestScope = scope;
    const sequence = ++operation.current;
    const active = () => mounted.current && current.current.canEdit && current.current.scope === requestScope && operation.current === sequence;
    setState(previous => ({ ...(previous.scope === scope ? previous : emptyState(scope)), busy: true, error: null, saved: false }));
    try {
      let configuration;
      if (save) {
        const response = await axiosGraphQL({
          query: UPDATE_CONSOLE_HELP_LINKS,
          variables: { expectedRevision: visible.revision, links: visible.draft.map(row => ({
            ...row, elUrl: row.elUrl.trim() || null, enUrl: row.enUrl.trim() || null,
          })) },
        }, { withoutClub: true, expectedFirebaseUid: auth.firebaseUser?.uid });
        configuration = response?.data?.data?.updateConsoleHelpLinks;
        if (!Number.isSafeInteger(configuration?.revision) || !Array.isArray(configuration.links)) throw new Error("invalid_help_configuration");
        if (!active()) return;
        // Refresh the shared header links immediately after saving.
        help.refresh().catch(() => {});
      } else {
        configuration = await help.refresh();
      }
      if (!active()) return;
      if (!configuration) {
        setState(previous => ({ ...previous, busy: false }));
        return;
      }
      const draft = createConsoleHelpDraft(configuration);
      setState({ scope, draft, revision: configuration.revision, original: JSON.stringify(draft), busy: false, conflict: false, error: null, saved: save });
    } catch (failure) {
      if (!active()) return;
      const conflict = failure?.codes?.includes("CONSOLE_HELP_LINKS_CONFLICT");
      const forbidden = failure?.codes?.includes("CONSOLE_HELP_LINKS_FORBIDDEN");
      setState(previous => ({ ...previous, busy: false, conflict: Boolean(conflict), error: conflict ? "page_help_conflict" : forbidden ? "page_help_forbidden" : save ? "page_help_save_error" : "page_help_load_error" }));
    }
  };

  if (!canEdit) return <main className="p-6"><StatusAlert severity="warning">{t("page_help_forbidden")}</StatusAlert></main>;
  const update = (pageKey, field, value) => setState(previous => ({
    ...previous, saved: false, draft: previous.draft.map(row => row.pageKey === pageKey ? { ...row, [field]: value } : row),
  }));
  const dirty = Boolean(visible.draft && JSON.stringify(visible.draft) !== visible.original);
  const error = visible.error || (!visible.draft && help?.error ? "page_help_load_error" : null);

  return <main className="flex max-w-[1120px] flex-1 flex-col gap-4 p-4 md:p-6">
    <PageHeader title={t("page_help_settings_title")} subtitle={t("page_help_settings_description")} />
    <StatusAlert severity="info">{t("page_help_language_note")}</StatusAlert>
    {error && <StatusAlert severity={visible.conflict ? "warning" : "error"}>{t(error)}</StatusAlert>}
    {visible.saved && <StatusAlert severity="success">{t("page_help_saved")}</StatusAlert>}
    <div className="flex flex-col gap-2 sm:flex-row">
      <Button type="button" disabled={!dirty || visible.busy || visible.conflict || hasInvalidConsoleHelpDraft(visible.draft || [])}
        onClick={() => run(true)}>{visible.busy ? <Spinner aria-label={t("page_help_saving")} /> : t("save")}</Button>
      <Button type="button" variant="outline" disabled={visible.busy || help?.loading} onClick={() => run(false)}>{t("page_help_reload")}</Button>
    </div>
    {(dirty || visible.conflict) && <p className="text-sm text-muted-foreground">{t("page_help_reload_note")}</p>}
    {!visible.draft && help?.loading && <Spinner aria-label={t("page_help_loading")} className="size-6" />}
    {visible.draft?.map(row => {
      const section = t(CONSOLE_HELP_PAGES.find(page => page.pageKey === row.pageKey).labelKey);
      const emptyEnabled = row.enabled && !row.elUrl.trim() && !row.enUrl.trim();
      return <Card key={row.pageKey} role="region" aria-label={section}>
        <CardHeader>
          <CardTitle role="heading" aria-level={2} className="text-lg">{section}</CardTitle>
          <CardAction>
            <Field orientation="horizontal">
              <Switch checked={row.enabled} disabled={visible.busy}
                onCheckedChange={checked => update(row.pageKey, "enabled", checked)}
                aria-label={`${section}: ${t("page_help_enabled")}`} />
              <FieldTitle aria-hidden="true">{t("page_help_enabled")}</FieldTitle>
            </Field>
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
        {emptyEnabled && <StatusAlert severity="warning">{t("page_help_url_required")}</StatusAlert>}
        <div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-2">
          {[{ field: "elUrl", label: "page_help_greek_url" }, { field: "enUrl", label: "page_help_english_url" }].map(({ field, label }) => {
            const normalized = normalizeConsoleHelpUrl(row[field]);
            const invalid = Boolean(row[field].trim() && !normalized);
            const previewLabel = `${section}: ${t("page_help_preview")} ${t(label)}`;
            return <div key={field} className="flex min-w-0 flex-col items-start gap-2">
              <LabeledTextField label={`${section}: ${t(label)}`} value={row[field]} disabled={visible.busy}
                onChange={event => update(row.pageKey, field, event.target.value)} aria-invalid={invalid}
                helperText={invalid ? t("page_help_invalid_url") : t("page_help_url_hint")}
                autoComplete="off" spellCheck={false} className="w-full" />
              {normalized && !visible.busy
                ? <a href={normalized} target="_blank" rel="noopener noreferrer" aria-label={previewLabel}
                  className={buttonVariants({ variant: "ghost", size: "sm" })}>
                  <ExternalLink aria-hidden="true" />{t("page_help_preview")}
                </a>
                : <Button type="button" variant="ghost" size="sm" disabled aria-label={previewLabel}>
                  <ExternalLink aria-hidden="true" />{t("page_help_preview")}
                </Button>}
            </div>;
          })}
        </div>
        </CardContent>
      </Card>;
    })}
  </main>;
}
